import { useState } from "react";
import type { EnrichedParticipant } from "../../types/participants";
import { supabase } from "../../lib/supabaseClient";
import ParticipantSelector from "./ParticipantSelector";

interface Props {
  tripId: string;
  participants: EnrichedParticipant[];
  onSaved: () => void;
  onClose: () => void;
}

export default function NewExpenseBottomSheet({ tripId, participants, onSaved, onClose }: Props) {
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Sonstiges");
  const [amount, setAmount] = useState("");
  const [payerId, setPayerId] = useState("");
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);
  const [distributeToAll, setDistributeToAll] = useState(true);
  const [saving, setSaving] = useState(false);

  const activeParticipants = participants.filter((p) => !p.is_blocked);

  const involvedUsers = distributeToAll
    ? activeParticipants.map((p) => p.user_id)
    : selectedParticipants;

  const getPayerName = () => {
    if (payerId === "reisekasse") return "💼 Reisekasse";
    const p = activeParticipants.find((ap) => ap.user_id === payerId);
    if (!p) return "–";
    return `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email;
  };

  const step1Valid = title.trim() !== "" && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0;
  const step2Valid = payerId !== "" && involvedUsers.length > 0;

  const handleSave = async () => {
    const parsedAmount = parseFloat(amount);
    if (!step1Valid || !step2Valid) return;

    setSaving(true);

    const { data, error } = await supabase
      .from("trip_expenses")
      .insert({
        trip_id: tripId,
        title: title.trim(),
        amount: parsedAmount,
        category,
        payer_id: payerId === "reisekasse" ? null : payerId,
      })
      .select()
      .single();

    if (error || !data) {
      console.error("Fehler beim Speichern:", error?.message);
      setSaving(false);
      return;
    }

    const share = parseFloat((parsedAmount / involvedUsers.length).toFixed(2));
    await supabase.from("trip_expense_shares").insert(
      involvedUsers.map((userId) => ({
        expense_id: data.id,
        user_id: userId,
        amount: share,
        is_paid: false,
      }))
    );

    setSaving(false);
    onSaved();
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <div className="fixed inset-x-0 bottom-16 z-50 bg-[#161b27] border-t border-[#2a3347] rounded-t-2xl shadow-xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-[#2a3347]" />
        </div>

        <div className="flex items-center justify-between px-5 pt-2 pb-4">
          <div className="flex gap-1.5 items-center">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-1 rounded-full transition-all duration-300 ${
                  s === step ? "w-8 bg-sky-400" : s < step ? "w-4 bg-sky-400/40" : "w-4 bg-[#2a3347]"
                }`}
              />
            ))}
            <span className="text-xs text-slate-500 ml-2">Schritt {step} / 3</span>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-300 text-xl transition-colors"
            aria-label="Schließen"
          >
            &times;
          </button>
        </div>

        <div className="px-5 pb-8">
          {step === 1 && (
            <>
              <h2 className="text-base font-semibold text-slate-100 mb-5">Betrag & Titel</h2>

              <label className="block mb-1 text-sm text-slate-400 font-medium">Betrag</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                autoFocus
                className="w-full bg-[#0d1117] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-3 text-2xl font-bold mb-4 focus:outline-none focus:border-sky-400 placeholder:text-slate-600"
              />

              <label className="block mb-1 text-sm text-slate-400 font-medium">Titel</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="z. B. Mittagessen"
                className="w-full bg-[#0d1117] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-4 focus:outline-none focus:border-sky-400 placeholder:text-slate-600"
              />

              <label className="block mb-1 text-sm text-slate-400 font-medium">Kategorie</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-[#0d1117] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-6 focus:outline-none focus:border-sky-400"
              >
                <option value="Reisen">Reisen</option>
                <option value="Übernachtung">Übernachtung</option>
                <option value="Verpflegung">Verpflegung</option>
                <option value="Aktivitäten">Aktivitäten</option>
                <option value="Workshops">Workshops</option>
                <option value="Sonstiges">Sonstiges</option>
              </select>

              <button
                onClick={() => setStep(2)}
                disabled={!step1Valid}
                className="w-full py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-medium text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Weiter →
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="text-base font-semibold text-slate-100 mb-5">Wer bezahlt & wer teilt?</h2>

              <label className="block mb-1 text-sm text-slate-400 font-medium">Wer hat bezahlt?</label>
              <select
                value={payerId}
                onChange={(e) => setPayerId(e.target.value)}
                className="w-full bg-[#0d1117] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm mb-5 focus:outline-none focus:border-sky-400"
              >
                <option value="">– Person auswählen –</option>
                <option value="reisekasse">💼 Reisekasse</option>
                {activeParticipants.map((p) => {
                  const name = `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email;
                  return (
                    <option key={p.user_id} value={p.user_id}>
                      {name}
                    </option>
                  );
                })}
              </select>

              <label className="block mb-2 text-sm text-slate-400 font-medium">Wer teilt die Ausgabe?</label>
              <ParticipantSelector
                participants={activeParticipants}
                selected={selectedParticipants}
                setSelected={setSelectedParticipants}
                distributeToAll={distributeToAll}
                setDistributeToAll={setDistributeToAll}
              />

              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 rounded-xl bg-[#1e2639] text-slate-300 text-sm hover:bg-[#2a3347] transition-colors"
                >
                  ← Zurück
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!step2Valid}
                  className="flex-1 py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Weiter →
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="text-base font-semibold text-slate-100 mb-5">Zusammenfassung</h2>

              <div className="bg-[#1e2639] border border-[#2a3347] rounded-xl p-4 mb-5 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Titel</span>
                  <span className="text-slate-100 font-medium">{title}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Betrag</span>
                  <span className="text-slate-100 font-bold text-base">
                    {parseFloat(amount).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Kategorie</span>
                  <span className="text-slate-300">{category}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Bezahlt von</span>
                  <span className="text-slate-300">{getPayerName()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Aufgeteilt auf</span>
                  <span className="text-slate-300">{involvedUsers.length} Person{involvedUsers.length !== 1 ? "en" : ""}</span>
                </div>
                {involvedUsers.length > 0 && (
                  <div className="flex justify-between text-sm border-t border-[#2a3347] pt-3">
                    <span className="text-slate-500">Pro Person</span>
                    <span className="text-sky-400 font-medium">
                      {(parseFloat(amount) / involvedUsers.length).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 py-3 rounded-xl bg-[#1e2639] text-slate-300 text-sm hover:bg-[#2a3347] transition-colors"
                >
                  ← Zurück
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 py-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {saving ? "Speichern…" : "✓ Speichern"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
